import PropTypes from 'prop-types';
import Decimal from 'decimal.js';
import sortBy from 'just-sort-by';
import { useState } from 'react';
import { numberFormat } from '@/utils/unitUtils';
import { smartRound } from '@/utils/miscUtils';

const palette = ['#1d4ed8', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#65a30d', '#475569', '#a16207'];

const toDecimal = value => {
  if (value == null) return new Decimal(0);
  if (value instanceof Decimal) return value;
  if (typeof value?.toNumber === 'function') return new Decimal(value.toNumber());
  return new Decimal(value);
};

const BarList = ({ title, subtitle, items, valueUnit, valueDigits = 2 }) => {
  const [expanded, setExpanded] = useState({});

  if (!items || items.length === 0) {
    return null;
  }

  const decimated = items.map(item => ({
    label: item.label,
    amount: toDecimal(item.amount),
    children: item.children ?? [],
  }));

  const positives = decimated.filter(d => d.amount.gt(0));
  const sorted = [
    ...sortBy(positives, d => -d.amount.toNumber()),
    ...sortBy(
      decimated.filter(d => d.amount.lte(0)),
      d => d.amount.toNumber(),
    ),
  ];

  const total = decimated.reduce((acc, d) => acc.plus(d.amount), new Decimal(0));

  const toggle = label => setExpanded(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between pb-1 border-b border-gray-100 mb-1">
        <div>
          {title && <h4 className="text-sm font-semibold text-gray-900">{title}</h4>}
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex text-xs text-gray-400">
          <div className="w-20 text-right">{valueUnit || ''}</div>
          <div className="w-12 text-right">Share</div>
        </div>
      </div>
      <ul className="flex flex-col">
        {sorted.map((row, idx) => {
          const isNegative = row.amount.lt(0);
          const share = total.eq(0) ? new Decimal(0) : row.amount.div(total).times(100);
          const widthPct = share.abs().toNumber();
          const color = palette[idx % palette.length];
          const hasChildren = row.children.length > 1;
          const isExpanded = !!expanded[row.label];
          return (
            <li key={row.label} className="border-b border-gray-50 last:border-b-0">
              <div
                className={`flex items-center gap-3 py-2 ${hasChildren ? 'cursor-pointer' : ''}`}
                onClick={hasChildren ? () => toggle(row.label) : undefined}
                role={hasChildren ? 'button' : undefined}
                tabIndex={hasChildren ? 0 : undefined}
                onKeyDown={hasChildren ? e => (e.key === 'Enter' || e.key === ' ') && toggle(row.label) : undefined}
                aria-expanded={hasChildren ? isExpanded : undefined}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1 mb-1">
                    {hasChildren && (
                      <svg
                        className="w-3.5 h-3.5 text-gray-400 shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d={isExpanded ? 'M19 9l-7 7-7-7' : 'M9 5l7 7-7 7'} />
                      </svg>
                    )}
                    <span className="text-sm text-gray-900 truncate" title={row.label}>
                      {row.label}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(widthPct, 1)}%`,
                        backgroundColor: color,
                        opacity: isNegative ? 0.5 : 1,
                      }}
                    />
                  </div>
                </div>
                <div className="w-20 text-right text-sm tabular-nums text-gray-900">
                  {isNegative ? '−' : ''}
                  {numberFormat(row.amount.abs().toNumber(), valueDigits)}
                </div>
                <div className="w-12 text-right text-sm tabular-nums text-gray-500">
                  {isNegative ? '−' : ''}
                  {smartRound(share.abs().toNumber())}%
                </div>
              </div>
              {isExpanded &&
                row.children.map(child => {
                  const childAmount = toDecimal(child.amount);
                  const childShare = total.eq(0) ? new Decimal(0) : childAmount.div(total).times(100);
                  const childNegative = childAmount.lt(0);
                  return (
                    <div key={child.label} className="flex items-center gap-3 py-1 pl-4 border-t border-gray-50">
                      <div className="flex-1 text-sm text-gray-600 truncate pl-3" title={child.label}>
                        {child.label}
                      </div>
                      <div className="w-20 text-right text-sm tabular-nums text-gray-700">
                        {childNegative ? '−' : ''}
                        {numberFormat(childAmount.abs().toNumber(), valueDigits)}
                      </div>
                      <div className="w-12 text-right text-sm tabular-nums text-gray-500">
                        {childNegative ? '−' : ''}
                        {smartRound(childShare.abs().toNumber())}%
                      </div>
                    </div>
                  );
                })}
            </li>
          );
        })}
      </ul>
      <div className="flex items-center pt-2 border-t border-gray-200 text-sm font-semibold text-gray-900">
        <div className="flex-1">Total</div>
        <div className="w-20 text-right tabular-nums">{numberFormat(total.toNumber(), valueDigits)}</div>
        <div className="w-12 text-right text-gray-500">100%</div>
      </div>
    </div>
  );
};

BarList.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.any.isRequired,
    }),
  ),
  valueUnit: PropTypes.string,
  valueDigits: PropTypes.number,
};

export default BarList;
